import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  submitLearningFeedbackAPI,
  getLearningFeedbackListAPI,
  getLearningFeedbackByIdAPI,
  deleteLearningFeedbackAPI,
} from "./learningFeedbackAPI";

export const submitLearningFeedback = createAsyncThunk(
  "learningFeedback/submitLearningFeedback",
  async (data, { rejectWithValue }) => {
    try {
      return await submitLearningFeedbackAPI(data);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to submit feedback");
    }
  }
);

export const getLearningFeedbackList = createAsyncThunk(
  "learningFeedback/getLearningFeedbackList",
  async ({ page = 1, limit = 10, search = "", from_date = "", to_date = "" } = {}, { rejectWithValue }) => {
    try {
      return await getLearningFeedbackListAPI({ page, limit, search, from_date, to_date });
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch feedback list");
    }
  }
);

export const getLearningFeedbackById = createAsyncThunk(
  "learningFeedback/getLearningFeedbackById",
  async (id, { rejectWithValue }) => {
    try {
      return await getLearningFeedbackByIdAPI(id);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch feedback details");
    }
  }
);

export const deleteLearningFeedback = createAsyncThunk(
  "learningFeedback/deleteLearningFeedback",
  async (id, { rejectWithValue }) => {
    try {
      return await deleteLearningFeedbackAPI(id);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to delete feedback");
    }
  }
);

const initialState = {
  submitLearningFeedback: { data: null, loading: false, error: null },
  getLearningFeedbackList: { data: [], total: 0, loading: false, error: null },
  getLearningFeedbackById: { data: null, loading: false, error: null },
  deleteLearningFeedback: { data: null, loading: false, error: null },
};

const buildAsyncReducers = (builder, thunk, stateKey, isListWithTotal = false) => {
  builder
    .addCase(thunk.pending, (state) => {
      state[stateKey].loading = true;
      state[stateKey].error = null;
    })
    .addCase(thunk.fulfilled, (state, action) => {
      state[stateKey].loading = false;
      if (isListWithTotal) {
        state[stateKey].data = action.payload?.data || [];
        state[stateKey].total = action.payload?.total || 0;
      } else {
        state[stateKey].data = action.payload?.data || action.payload;
      }
    })
    .addCase(thunk.rejected, (state, action) => {
      state[stateKey].loading = false;
      state[stateKey].error = action.payload;
    });
};

const learningFeedbackSlice = createSlice({
  name: "learningFeedback",
  initialState,
  reducers: {
    clearSubmitLearningFeedback: (state) => {
      state.submitLearningFeedback = { data: null, loading: false, error: null };
    },
    clearLearningFeedbackList: (state) => {
      state.getLearningFeedbackList = { data: [], total: 0, loading: false, error: null };
    },
    clearLearningFeedbackById: (state) => {
      state.getLearningFeedbackById = { data: null, loading: false, error: null };
    },
    clearDeleteLearningFeedback: (state) => {
      state.deleteLearningFeedback = { data: null, loading: false, error: null };
    },
  },
  extraReducers: (builder) => {
    buildAsyncReducers(builder, submitLearningFeedback, "submitLearningFeedback");
    buildAsyncReducers(builder, getLearningFeedbackList, "getLearningFeedbackList", true);
    buildAsyncReducers(builder, getLearningFeedbackById, "getLearningFeedbackById");
    buildAsyncReducers(builder, deleteLearningFeedback, "deleteLearningFeedback");
  },
});

export const {
  clearSubmitLearningFeedback,
  clearLearningFeedbackList,
  clearLearningFeedbackById,
  clearDeleteLearningFeedback,
} = learningFeedbackSlice.actions;

export default learningFeedbackSlice.reducer;
